// After a deploy, open tabs still reference the old build's chunks which no longer exist on the
// server. Reloading once fetches the new index.html and its chunks.

const RELOAD_KEY = "chunk-reload-build";

const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /Unable to preload CSS/i,
];

export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? "");
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

// The entry script name is hashed per build, so it identifies the build this page runs
function currentBuild(): string {
  return document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.src ?? "";
}

/**
 * Reload the page, to `path` if given, at most once per build. If the page is still on the
 * same build after reloading (e.g. a cached index.html) the chunk is missing for another
 * reason and reloading again would loop. Returns false when the reload was skipped
 * (or storage is unavailable) so the caller can surface the error.
 */
export function reloadOnce(path?: string): boolean {
  const build = currentBuild();
  try {
    if (sessionStorage.getItem(RELOAD_KEY) === build) return false;
    sessionStorage.setItem(RELOAD_KEY, build);
  } catch {
    // Without storage there is no loop guard, so don't reload at all
    return false;
  }
  if (path) {
    window.location.assign(path);
  } else {
    window.location.reload();
  }
  return true;
}
